import type {
  PostingsDataQuery,
  PostingsGetActiveQuery,
  PostingsV3DataPoint,
  PostingsV3DataQuery,
  PostingsV3Ent,
} from '@revelio/data-access';

type PostingResponse = NonNullable<PostingsDataQuery['posting']>;
type ActivePostingResponse = NonNullable<PostingsGetActiveQuery['posting']>;

type PostingsV3Entity = NonNullable<
  PostingsV3DataQuery['postings_v3']
>['entities'][number];

const buildCategoryPoints = ({
  volume,
  timeToFill,
  weekRefs,
}: {
  volume: ReadonlyArray<PostingsV3DataPoint>;
  timeToFill: ReadonlyArray<PostingsV3DataPoint>;
  weekRefs: ReadonlyMap<number, string>;
}): NonNullable<PostingResponse[number]>['category'] => {
  const timeToFillById = new Map<number, number>();
  for (const point of timeToFill) {
    timeToFillById.set(point.id, point.val);
  }

  return volume
    .map((point) => {
      const date = weekRefs.get(point.id);
      if (date === undefined) return null;
      return {
        date,
        active: point.val,
        time_to_fill: timeToFillById.get(point.id) ?? 0,
      };
    })
    .filter(
      (
        entry
      ): entry is { date: string; active: number; time_to_fill: number } =>
        entry !== null
    )
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((entry) => ({
      metadata: { shortName: entry.date },
      metrics: { active: entry.active, time_to_fill: entry.time_to_fill },
    }));
};

export const convertPostingsV3ToMarketPostings = ({
  data,
  weekRefs,
}: {
  data: PostingsV3DataQuery | undefined;
  weekRefs: ReadonlyMap<number, string>;
}): PostingResponse => {
  const entities = data?.postings_v3?.entities ?? [];

  return entities.map((entity: PostingsV3Entity): PostingResponse[number] => ({
    metadata: {
      id: entity.index,
      shortName: entity.short_name,
      longName: entity.long_name,
    },
    category: buildCategoryPoints({
      volume: entity.metrics.active,
      timeToFill: entity.metrics.time_to_fill,
      weekRefs,
    }),
  }));
};

export const convertPostingsV3ToActivePostings = ({
  data,
  requestEntities,
  weekRefs,
}: {
  data: PostingsV3DataQuery | undefined;
  requestEntities: ReadonlyArray<PostingsV3Ent>;
  weekRefs: ReadonlyMap<number, string>;
}): ActivePostingResponse => {
  const entities = data?.postings_v3?.entities ?? [];

  return entities
    .map((entity: PostingsV3Entity): ActivePostingResponse[number] => {
      const requestEntity = requestEntities[entity.index];
      if (!requestEntity) return null;

      const category = entity.metrics.active
        .map((point) => {
          const date = weekRefs.get(point.id);
          if (date === undefined) return null;
          return {
            metadata: { shortName: date },
            metrics: { active: point.val },
          };
        })
        .filter(
          (
            entry
          ): entry is {
            metadata: { shortName: string };
            metrics: { active: number };
          } => entry !== null
        );

      return {
        metadata: { id: requestEntity.EntID },
        category,
      };
    })
    .filter(
      (entry): entry is NonNullable<ActivePostingResponse[number]> =>
        entry !== null
    );
};
